import { Injectable } from '@angular/core';
import {Observable, Subject, throwError} from "rxjs";
import {select, Store} from "@ngrx/store";
import { selectSubscriptionData } from "../state/subscription/subscription.selectors";
import {catchError, map, switchMap, takeUntil} from "rxjs/operators";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {AuthService} from "./auth.service";
import {environment} from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  private apiUrl = environment.api.url;
  private cancelRequests$ = new Subject<void>();

  token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private store: Store,
  ) { }

  subscriptionData(): Observable<any | null> {
    return this.store.pipe(select(selectSubscriptionData));
  }

  // OLD IMPLEMENTATION - Commented out (migrated to new API endpoint /api/v1/credits/balance)
  // getCredits(): Observable<{ credits: number; ai_tokens: number; user: any; message: string }> {
  //   return this.authService.token().pipe(
  //     takeUntil(this.cancelRequests$),
  //     switchMap(token => {
  //       this.token = token;
  //       const httpOptions = {
  //         headers: new HttpHeaders({
  //           'Content-Type': 'application/json',
  //           'Authorization': `Bearer ${this.token}`
  //         })
  //       };
  //       const url = `${this.apiUrl}/users/credits`; // OLD ENDPOINT
  //       return this.http.get<any>(url, httpOptions).pipe(
  //         takeUntil(this.cancelRequests$),
  //         map(response => {
  //           if (!response.data) {
  //             throw new Error('Fetching credits failed: ' + response.message);
  //           }
  //           return {
  //             credits: response.data.credits,
  //             ai_tokens: response.data.ai_tokens,
  //             user: response.data.user,
  //             message: response.metadata.message
  //           };
  //         }),
  //         catchError(error => {
  //           console.error('Error in getting credits', error);
  //           return throwError(error);
  //         })
  //       );
  //     })
  //   );
  // }

  /**
   * Get user credits balance from new API endpoint
   * New endpoint: /api/v1/credits/balance
   * Returns current_balance, allocation dates, and subscription amount
   */
  getCredits(): Observable<{
    current_balance: number;
    last_allocation_date: string;
    next_allocation_date: string;
    subscription_amount: number;
    credits: number; // For backward compatibility
    ai_tokens?: number;
    user?: any;
    message: string;
  }> {
    return this.authService.token().pipe(
      // Cancel token request if cancelRequests$ emits
      takeUntil(this.cancelRequests$),
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        // NEW API ENDPOINT (apiUrl already contains /api/v1)
        const url = `${this.apiUrl}/credits/balance`;

        return this.http.get<any>(url, httpOptions).pipe(
          // Cancel HTTP request if cancelRequests$ emits
          takeUntil(this.cancelRequests$),
          map(response => {
            if (!response.data) {
              throw new Error('Fetching credits failed: ' + (response.metadata?.message || 'Unknown error'));
            }

            // Map new API response structure
            return {
              current_balance: response.data.current_balance,
              last_allocation_date: response.data.last_allocation_date,
              next_allocation_date: response.data.next_allocation_date,
              subscription_amount: response.data.subscription_amount,
              credits: response.data.current_balance, // For backward compatibility
              ai_tokens: response.data.ai_tokens || 0,
              user: response.data.user || null,
              message: response.metadata.message
            };
          }),
          catchError(error => {
            console.error('Error in getting credits', error);
            return throwError(error);
          })
        );
      })
    );
  }

  /**
   * Cancel all ongoing HTTP requests
   * Call this method when logging out
   */
  cancelRequests(): void {
    this.cancelRequests$.next();
  }
}
